import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Settings,
  Save,
  Sliders,
  MessageSquare,
  Shield
} from 'lucide-react';

export const SettingsPage = () => {
  const api = useApi();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [capabilities, setCapabilities] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, capsRes] = await Promise.all([
        api.get('/settings'),
        api.get('/channels/capabilities')
      ]);
      setSettings(settingsRes.data);
      setCapabilities(capsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/settings', settings);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <MainLayout>
        <Header title="Paramètres" />
        <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse-soft text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  const isAdmin = user?.role === 'admin';

  return (
    <MainLayout>
      <Header title="Paramètres" subtitle="Configuration globale de l'application" />

      <div className="p-6 space-y-6 animate-fadeIn max-w-4xl" data-testid="settings-page">
        {!isAdmin && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Shield className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800 text-sm">
                Seuls les administrateurs peuvent modifier les paramètres.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Thresholds */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sliders className="h-5 w-5 text-[#FF5A6E]" />
              Seuils IA
            </CardTitle>
            <CardDescription>Configurez les seuils de décision automatique</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Seuil de confiance</Label>
                  <span className="text-sm font-medium text-[#FF5A6E]">
                    {Math.round((settings?.confidence_threshold || 0.7) * 100)}%
                  </span>
                </div>
                <Slider
                  value={[settings?.confidence_threshold || 0.7]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={([v]) => updateSetting('confidence_threshold', v)}
                  disabled={!isAdmin}
                  className="w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  En dessous de ce seuil, une revue humaine est requise
                </p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Seuil de risque</Label>
                  <span className="text-sm font-medium text-amber-600">
                    {Math.round((settings?.risk_threshold || 0.3) * 100)}%
                  </span>
                </div>
                <Slider
                  value={[settings?.risk_threshold || 0.3]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={([v]) => updateSetting('risk_threshold', v)}
                  disabled={!isAdmin}
                  className="w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Au-dessus de ce seuil, le job est marqué comme risqué
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Text settings */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#2E86DE]" />
              Paramètres de rédaction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tonalité par défaut</Label>
                <Select
                  value={settings?.default_tone || 'standard'}
                  onValueChange={(v) => updateSetting('default_tone', v)}
                  disabled={!isAdmin}
                >
                  <SelectTrigger data-testid="default-tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="chaleureux">Chaleureux</SelectItem>
                    <SelectItem value="ferme_diplomatique">Ferme diplomatique</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Longueur max avis (caractères)</Label>
                <Input
                  type="number"
                  value={settings?.max_review_length || 500}
                  onChange={(e) => updateSetting('max_review_length', parseInt(e.target.value))}
                  disabled={!isAdmin}
                  data-testid="max-review-length-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Longueur max réponse (caractères)</Label>
                <Input
                  type="number"
                  value={settings?.max_reply_length || 300}
                  onChange={(e) => updateSetting('max_reply_length', parseInt(e.target.value))}
                  disabled={!isAdmin}
                  data-testid="max-reply-length-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Workflow settings */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-600" />
              Workflow
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Validation humaine obligatoire</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Tous les brouillons nécessiteront une validation manuelle
                </p>
              </div>
              <Switch
                checked={settings?.human_review_always || false}
                onCheckedChange={(v) => updateSetting('human_review_always', v)}
                disabled={!isAdmin}
                data-testid="human-review-switch"
              />
            </div>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between opacity-50">
                <div>
                  <Label>Publication automatique (désactivé)</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Non supporté par les APIs Airbnb/Booking.com
                  </p>
                </div>
                <Switch checked={false} disabled />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Channel capabilities */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg">Capacités par canal</CardTitle>
            <CardDescription>Fonctionnalités supportées par chaque plateforme</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {capabilities.map((cap) => (
                <div key={cap.channel} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900 capitalize">{cap.channel}</span>
                    <div className="flex gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        cap.supports_auto_publish_review ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                      }`}>
                        {cap.supports_auto_publish_review ? '✓' : '✗'} Auto-publish avis
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        cap.supports_auto_publish_reply ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                      }`}>
                        {cap.supports_auto_publish_reply ? '✓' : '✗'} Auto-publish réponse
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        cap.supports_fetch_messages ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                      }`}>
                        {cap.supports_fetch_messages ? '✓' : '✗'} Messages
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">{cap.notes}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Save button */}
        {isAdmin && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>Enregistrement...</>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Enregistrer les paramètres
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
